import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Col, Row } from 'reactstrap';

import { CHECKBOX_TYPE_KEYS } from '../../../constants/Filter';

import i18n from '../../../i18n';

import { CommonFilter } from './CommonFilterPopup';
import CheckboxInput from '../../../common/inputs/CheckboxInput';
import useHandleEnter from '../../../common/hooks/useHandleEnter';
import { STATUS_REASONS_MAP } from "../../Status/ChangeReasonOptions/ChangeReasonOptions"

const initCheckboxState = (initialValue) => {
    const checkboxState = {};
    if (Array.isArray(initialValue)) {
        initialValue.forEach((point) => checkboxState[point] = true);
    }
    else if (['string', 'number'].includes(typeof initialValue)) {
        checkboxState[initialValue] = true;
    }
    return checkboxState;
}

export const FilterCheckbox = ({ activePopup, onSelect, onCancel }) => {
    const [checkboxState, setCheckboxState] = useState(initCheckboxState(activePopup?.initialValue));
    const [reasonsState, setReasonsState] = useState({});

    const isAccountStatusPopUp = activePopup.key === 'account_status';

    const isEnabled = useMemo(
        () =>
            !!Object.keys(checkboxState).filter(item => checkboxState[item])
                .length,
        [activePopup.initialValue, checkboxState]
    );

    const handleReasonChange = useCallback(
      (statusKey, reason) => (checked) => {
          setReasonsState(prev => {
              const currentReasons = prev[statusKey] || [];
              return {
                  ...prev,
                  [statusKey]: checked
                    ? [...currentReasons, reason]
                    : currentReasons.filter(r => r !== reason),
              };
          });
      },
      []
    );

    const handleChange = useCallback(
        key => value => {
            if (key === CHECKBOX_TYPE_KEYS.ALL) {
                const newCheckboxState = activePopup.list.reduce(
                    (acc, { key }) => ({
                        ...acc,
                        [key]: value
                    }),
                    {}
                );
                setCheckboxState({ ...newCheckboxState, [key]: value });
            } else {
                setCheckboxState(prev => ({
                    ...prev,
                    [key]: value,
                }));
            }
        },
        [activePopup.list]
    );

    const onSave = useCallback(() => {
        const statuses = Object.keys(checkboxState)
          .filter(item => item !== CHECKBOX_TYPE_KEYS.ALL && checkboxState[item]);

        let state = [];
        let allReasons = [];

        statuses.forEach(status => {
            state.push({ key: activePopup.key, value: status });

            if (isAccountStatusPopUp && STATUS_REASONS_MAP[status]) {
                const reasons = reasonsState[status] || [];
                if (reasons.length) {
                    allReasons = [...allReasons, ...reasons];
                }
            }
        });

        if (allReasons.length) {
            const uniqueReasons = [...new Set(allReasons)];
            state.push({
                key: 'account_status_change_reason',
                value: uniqueReasons
            });
        }

        onSelect(state);
    }, [checkboxState, reasonsState]);

    useHandleEnter(onSave);

    return (
        <CommonFilter
            title={activePopup.title}
            className={'filter-player_type'}
            onCancel={onCancel}
        >
            <Row
                className={'filter-player_type-row filter-player_type-all'}
            >
                <Col>
                    <CheckboxInput
                        checked={checkboxState[CHECKBOX_TYPE_KEYS.ALL]}
                        onChange={handleChange(CHECKBOX_TYPE_KEYS.ALL)}
                    >
                        {i18n.t(`filter.constant.${CHECKBOX_TYPE_KEYS.ALL}`)}
                    </CheckboxInput>
                </Col>
            </Row>

            {activePopup.list?.map(({ label, isDisabled = false, key }, index) => (
              <React.Fragment key={index}>
                  <Row className={'filter-player_type-row'}>
                      <Col>
                          <CheckboxInput
                            checked={checkboxState[key]}
                            onChange={handleChange(key)}
                            disabled={isDisabled}
                          >
                              {label}
                          </CheckboxInput>
                      </Col>
                  </Row>

                  {isAccountStatusPopUp && checkboxState[key] && STATUS_REASONS_MAP[key] && (
                    <div style={{ marginLeft: '1.5rem' }}>
                        {STATUS_REASONS_MAP[key].map((reason, idx) => (
                          <Row key={idx} className="filter-player_type-row">
                              <Col>
                                  <CheckboxInput
                                    checked={reasonsState[key]?.includes(reason)}
                                    onChange={handleReasonChange(key, reason)}
                                  >
                                      {reason}
                                  </CheckboxInput>
                              </Col>
                          </Row>
                        ))}
                    </div>
                  )}
              </React.Fragment>
            ))}

            <Button color={'primary'} disabled={!isEnabled} onClick={onSave}>
                {i18n.t('filter.apply')}
            </Button>
        </CommonFilter>
    );
};

FilterCheckbox.propTypes = {
    activePopup: PropTypes.object.isRequired,
    onSelect: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired
};
